import { z } from 'zod';
import {
  baseQueryArgs,
  baseExtraOptions,
  rsosApi,
} from '@rsos/reduxDataStore/src/features/rsosApi';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import { useUnreadConversationCount } from '../../contexts/ConversationsContext';

const InteragencyDirectoryEntrySchema = z.object({
  account_id: z.string(),
  display_name: z.string(),
});

const InteragencyDirectoryResponseSchema = z.object({
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  agencies: z.array(InteragencyDirectoryEntrySchema),
});

const InteragencyParticipantSchema = z.object({
  account_id: z.string(),
  authority_display_name: z.string(),
  joined_at: z.number().nullable().optional(),
  user: z
    .object({
      talkjs_id: z.string().nullable().optional(),
      locale: z.string().nullable().optional(),
      name: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});

const InteragencyConversationExtraInfoSchema = z.object({
  conversation_id: z.string(),
  conversation_name: z.string(),
  status: z.string(),
  creation_time: z.number(),
  last_updated_time: z.number(),
  participants: z.array(InteragencyParticipantSchema),
});

const InteragencyConversationItemSchema = z.object({
  source: z.string(),
  application: z.string(),
  conversation_id: z.string(),
  conversation_name: z.string(),
  conversation_start_time: z.number(),
  caller_id: z.string().nullable().optional(),
  valid_for_psap: z.boolean().nullable().optional(),
  location_data: z.record(z.unknown()).nullable().optional(),
  extra_info: InteragencyConversationExtraInfoSchema,
});

const InteragencyConversationsListResponseSchema = z.object({
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  conversations: z.array(InteragencyConversationItemSchema),
});

const InteragencyConversationDeleteResponseSchema = z.object({
  conversation_id: z.string(),
  deleted: z.number().int().min(0),
});

const CreateInteragencyConversationResponseSchema =
  InteragencyConversationItemSchema;

export type InteragencyDirectoryEntry = z.infer<
  typeof InteragencyDirectoryEntrySchema
>;
export type InteragencyParticipant = z.infer<
  typeof InteragencyParticipantSchema
>;
export type InteragencyConversationItem = z.infer<
  typeof InteragencyConversationItemSchema
>;
export type InteragencyConversationsListResponse = z.infer<
  typeof InteragencyConversationsListResponseSchema
>;
export type CreateInteragencyConversationResponse = z.infer<
  typeof CreateInteragencyConversationResponseSchema
>;
export type InteragencyConversationDeleteResponse = z.infer<
  typeof InteragencyConversationDeleteResponseSchema
>;

const InteragencyTags = {
  Directory: 'InteragencyDirectory',
  Conversation: 'InteragencyConversation',
} as const;

const enhancedApi = rsosApi.enhanceEndpoints({
  addTagTypes: Object.values(InteragencyTags),
});

/**
 * RTK Query endpoints for the Interagency Chat feature (SOUP-1110+).
 * Backed by the text_to_911 service's `/interagency/{org}/*` routes.
 *
 * These endpoints power the Agency Chat tab in the call queue: browsing
 * available agencies, creating conversations, and listing existing ones.
 * Conversation messaging itself is handled separately via Chat JS — this
 * layer only manages conversation lifecycle and directory lookups.
 *
 * Per-conversation last-message and sender data come from the TalkJS client
 * SDK via `InteragencyConversationsContext`; no backend endpoints here fetch
 * message content.
 *
 * All responses are validated with Zod in the transformResponse handler. This
   (1) validates the payload is expected (RTK will handle the errors if .parse
   throws and pipe them through to the "use...Query" hooks) and (2) strongly
   types the result of each query to the exact type of the zod schema.
 * Tag invalidation keeps the conversation list in sync after mutations.
 */
export const interagencyAPI = enhancedApi.injectEndpoints({
  endpoints: builder => ({
    getInteragencyDirectory: builder.query({
      query: ({ orgName }: { orgName: string }) => ({
        ...baseQueryArgs,
        url: `v1/text_to_911/interagency/${orgName}/directory`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => {
        const parsed = InteragencyDirectoryResponseSchema.parse(response);
        return parsed.agencies;
      },
      providesTags: [InteragencyTags.Directory],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    createInteragencyConversation: builder.mutation({
      query: ({
        orgName,
        participants,
        conversation_name,
      }: {
        orgName: string;
        participants: {
          account_id: string;
          authority_display_name: string;
        }[];
        conversation_name?: string;
      }) => ({
        ...baseQueryArgs,
        url: `v1/text_to_911/interagency/${orgName}/conversations`,
        method: 'POST',
        body: { participants, conversation_name },
      }),
      transformResponse: (response: unknown) => {
        return CreateInteragencyConversationResponseSchema.parse(response);
      },
      // invalidate all to force listInteragencyConversations to refetch
      invalidatesTags: [InteragencyTags.Conversation],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    listInteragencyConversations: builder.query({
      query: ({ orgName }: { orgName: string }) => ({
        ...baseQueryArgs,
        url: `v1/text_to_911/interagency/${orgName}/conversations`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => {
        return InteragencyConversationsListResponseSchema.parse(response);
      },
      providesTags: result =>
        // If there is a result, tag each conversation's data. If not, we got an empty list so we use the general tag.
        result
          ? [
              ...result.conversations.map(({ conversation_id }) => ({
                type: InteragencyTags.Conversation,
                id: conversation_id,
              })),
              InteragencyTags.Conversation,
            ]
          : [InteragencyTags.Conversation],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    // delete should never be exposed to the user outside debug-panel
    deleteInteragencyConversation: builder.mutation({
      query: ({
        orgName,
        conversationId,
      }: {
        orgName: string;
        conversationId: string;
      }) => ({
        ...baseQueryArgs,
        url: `v1/text_to_911/interagency/${orgName}/conversations/${conversationId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) => {
        return InteragencyConversationDeleteResponseSchema.parse(response);
      },
      // invalidate all to force listInteragencyConversations to refetch
      invalidatesTags: [InteragencyTags.Conversation],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useGetInteragencyDirectoryQuery,
  useCreateInteragencyConversationMutation,
  useListInteragencyConversationsQuery,
  useDeleteInteragencyConversationMutation,
} = interagencyAPI;

export const useInteragencyConversationById = (
  conversationId: string | null,
) => {
  const orgName = useCurrentOrgName();
  const { data } = useListInteragencyConversationsQuery(
    { orgName: orgName ?? '' },
    { skip: !orgName || !conversationId },
  );
  return (
    data?.conversations.find(c => c.conversation_id === conversationId) ?? null
  );
};

/**
 * Number of interagency conversations with unread messages for the current
 * user, scoped to the conversations the backend actually lists for this org.
 *
 * This is the interagency-specific "strategy" for unread counting: it pairs
 * the access-scoped conversation list (the authoritative set of conversations
 * the user is allowed to see) with the domain-agnostic
 * `useUnreadConversationCount`. Passing the listed ids in keeps stale TalkJS
 * unreads — conversations deleted server-side or belonging to another
 * authority — from inflating the badge. Other chat types would compose their
 * own list source the same way.
 */
export function useInteragencyUnreadConversationCount(): number {
  const orgName = useCurrentOrgName();
  const { data } = useListInteragencyConversationsQuery(
    { orgName: orgName ?? '' },
    { skip: !orgName },
  );
  const conversationIds = data?.conversations.map(c => c.conversation_id) ?? [];
  return useUnreadConversationCount(conversationIds);
}

/** Returns a redux action that invalidate the list of interagency conversations. */
export function invalidateInteragencyConversationList() {
  return interagencyAPI.util.invalidateTags([InteragencyTags.Conversation]);
}
